import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useMutation } from 'Shared/Hooks/useApolloClient'
import { asyncHandler } from 'Utils/asyncHelpers'
import { notifyError } from 'Utils/errorNotifications'
import { Button } from 'reactstrap'
import stripePaymentIntentCreateMutation from 'POS/Hooks/Queries/stripePaymentIntentCreateMutation'
import { useTerminalInvoiceSubscription } from 'AdminInvoices/Hooks/useTerminalInvoiceSubscription'
import { fetchInstallmentStatus } from 'Admin/Store/Slices/invoicesSlice'
import useStripeTerminal from 'POS/Hooks/useStripeTerminal'
import PaymentFormErrors from 'POS/Containers/Checkout/PaymentFormErrors'
import TerminalStatus from 'POS/Containers/TerminalStatus/TerminalStatus'
import TerminalStepsModal from 'POS/Containers/Checkout/TerminalStepsModal'
import FontAwesomeIcon from 'Shared/FontAwesomeIcon/FontAwesomeIcon'
import PropTypes from 'prop-types'
import 'Styles/Button.scss'

const TerminalPayment = ({ campgroundId, onComplete, installmentId, section, toggle, total, userId }) => {
  const terminalBoxClasses = 'd-flex terminal-box justify-content-between shadow-sm py-2 px-3 px-lg-4 rounded w-100 bg-white border mb-4'
  const { clearError, error, getTerminal, handleError, isConnected, terminal, isConnecting } = useStripeTerminal()
  const dispatch = useDispatch()
  const { isCompleted } = useTerminalInvoiceSubscription()
  const [createStripePaymentIntent,
        { data: dataPaymentIntentCreate, loading: loadingPaymentIntentCreate,
          error: errorPaymentIntentCreate }] = useMutation(stripePaymentIntentCreateMutation)
  const [insertCardNow, setInsertCardNow] = useState(false)
  const [processingPayment, setProcessingPayment] = useState(false)
  const [showTerminalStepsModal, setShowTerminalStepsModal] = useState(false)
  const [errors] = useState({})
  const modalText = section === 'GeneralStore' ? 'Processing Purchase' : 'Creating Reservation'


  useEffect(() => {
    const showTerminalSteps = error?.length > 0 || processingPayment || insertCardNow
    setShowTerminalStepsModal(showTerminalSteps)
  }, [error, processingPayment, insertCardNow])

  useEffect(() => {
    if (isCompleted) {
      dispatch(fetchInstallmentStatus())
      setProcessingPayment(false)
      toggle()
    }
  }, [isCompleted])

  const onClose = async () => {
    clearError()
    setProcessingPayment(false)
    setInsertCardNow(false)

    if (insertCardNow && terminal?.cancelCollectPaymentMethod)
      try {
        await terminal.cancelCollectPaymentMethod()
        setInsertCardNow(false)
      } catch (err) {
        notifyError('Failed to cancel collectPaymentMethod:', err)
      }
  }

  const onCloseWithRetry = async () => {
    await onClose()

    if (!isConnected) getTerminal()
  }

  const collectPaymentMethod = asyncHandler(async clientSecret => {
    setInsertCardNow(true)
    const result = await terminal.collectPaymentMethod(clientSecret)
    // TODO: handle process failures https://stripe.com/docs/terminal/payments#handling-processing-failures
    if (result.error) return handleError('Payment Method Collection Error:', result.error)

    setInsertCardNow(false)
    processPayment(result.paymentIntent)
  })

  const fetchClientSecret = async () => {
    const variables = { params: { campgroundId, installmentId, isAdminInvoices: section === 'Invoice Details',
                                  terminal: true, total: total?.amount, userId } }
    createStripePaymentIntent({ variables })
  }

  useEffect(() => {
    if (errorPaymentIntentCreate && !loadingPaymentIntentCreate)
      handleError(`Payment Intent Creation Error: ${errorPaymentIntentCreate}`)
  }, [errorPaymentIntentCreate])

  useEffect(() => {
    if (dataPaymentIntentCreate && !loadingPaymentIntentCreate && !errorPaymentIntentCreate) {
      const { paymentIntent, error } = dataPaymentIntentCreate?.stripePaymentIntentCreate || {}
      if (error || !paymentIntent) return handleError(`Client Secret Fetch Error: ${error}`)

       collectPaymentMethod(paymentIntent.clientSecret)
    }
  }, [dataPaymentIntentCreate])

  const processPayment = asyncHandler(async paymentIntent => {
    setProcessingPayment(true)

    const result = await terminal.processPayment(paymentIntent)
    if (result.error) {
      setProcessingPayment(false)
      return handleError('Payment Process Error:', result.error)
    }
    const variables = { paymentIntentId: result.paymentIntent.id, useCardOnFile: false, paymentSource: 'terminal',
                        saveCardOnFile: false }
    onComplete(variables)
  })

  const toggleTerminalStepsModal = () => setShowTerminalStepsModal(prevState => !prevState)

  return (
    <>
      {!!Object.keys(errors).length && <PaymentFormErrors errors={errors} />}

      <div className={terminalBoxClasses}>
        <div className="d-grid">
          <FontAwesomeIcon className="m-0" color="#5433FF" icon={['fab', 'stripe']} size="3x" />
          <TerminalStatus isConnected={isConnected} isConnecting={isConnecting} />
        </div>

        <div className="d-flex align-items-center">
          <Button className="fw-bold" color="stripe-blue" disabled={isConnecting || !isConnected}
                  onClick={fetchClientSecret}>
            Pay with Terminal

            <FontAwesomeIcon className="ms-2" icon="square-arrow-up-right" color="white" />
          </Button>
        </div>
      </div>

      <TerminalStepsModal {...{ error, errors, insertCardNow, isConnected, modalText, onClose, onCloseWithRetry,
                                processingPayment }} toggle={toggleTerminalStepsModal}
                                show={showTerminalStepsModal} />
    </>
  )
}

TerminalPayment.defaultProps = {
    campgroundId: '',
    userId: undefined,
    total: {
      amount: 0.0,
      display: '',
    },
    section: '',
  }

TerminalPayment.propTypes = {
  campgroundId: PropTypes.string,
  userId: PropTypes.number,
  total: PropTypes.shape({
      amount: PropTypes.number,
      display: PropTypes.string,
    }),
  section: PropTypes.string,
}

export default TerminalPayment
